import clubAPI from "../../api/clubAPI";
import { updateAllAppParams } from "../../store/appParamSlice";

// One request at a time, across every screen that needs it. The appParamsLoaded check
// callers make can't do this on its own: it only flips once the response lands, so a
// second caller during the flight sees false and issues its own — which is exactly what
// the welcome prefetch and the home page were doing to each other when the user tapped
// the CTA before the first response arrived.
let inFlight = null;

/**
 * Fetches the club list (GetWebFullData) into appParamSlice, at most once concurrently.
 *
 * Returns the in-flight promise to a second caller rather than turning it away, so it
 * still gets the result instead of having to ask again.
 *
 * Deliberately not tied to a component's lifetime: the payload belongs to the store, and
 * whichever screen mounts next wants it. The two callers used to abort their own dispatch
 * on unmount, which threw away the prefetch precisely when the user moved on quickly.
 *
 * Never throws — a failure leaves appParamSlice untouched and is logged, not surfaced.
 *
 * @param {{binID: number|string, token: string, id: string, phone: string, dispatch: Function}} params
 * @returns {Promise<void>}
 */
export default function fetchAppParams({ binID, token, id, phone, dispatch }) {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const response = await clubAPI.getWebFullData({ binID, token, id, phone });
      if (response.data.Error !== 0) return;
      dispatch(updateAllAppParams(JSON.parse(response.data.Data)));
    } catch (err) {
      console.error("Club list fetch failed:", err);
    } finally {
      // Cleared so a failed attempt can be retried by the next screen that needs it.
      inFlight = null;
    }
  })();

  return inFlight;
}
